import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import StatsBar from '../ui/StatsBar';

// ===========================================================================
// HELPER FUNCTIONS
// ===========================================================================

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
};

const formatDateTime = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDaysAgo = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const dateObj = new Date(date);
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
};

const formatTimeOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const isHighValue = (amount) => {
  return amount && amount > 50000;
};

const getStageClass = (stageId) => {
  const stageMap = {
    '29682770': 'stage-1',
    '29682771': 'stage-2',
    '29682772': 'stage-3',
    '29682773': 'stage-4',
    '29682774': 'stage-5',
    '29682775': 'stage-won',
    '29682776': 'stage-lost'
  };
  return stageMap[String(stageId)] || 'stage-1';
};

const getAutotaskOpportunityUrl = (opportunityId) => {
  return `https://ww29.autotask.net/Mvc/CRM/OpportunityDetail.mvc?ids%5B0%5D=${opportunityId}&OpportunityId=${opportunityId}`;
};

const getHubSpotDealUrl = (dealId) => {
  return `https://app.hubspot.com/contacts/23367316/deal/${dealId}`;
};

const getSyncStatusLabel = (status) => {
  const labels = {
    'synced': '✓ Synced',
    'success': '✓ Synced',
    'pending': '⏳ Pending',
    'error': '✕ Error'
  };
  return labels[status] || '⏳ Pending';
};

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================

const Opportunities = ({ opportunities, stats, loading, pagination, fetchOpportunities, apiFetch }) => {
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [syncHistory, setSyncHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingRetry, setLoadingRetry] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const [filters, setFilters] = useState({
    stage: 'all',
    syncStatus: 'all',
    minAmount: '',
    maxAmount: '',
    searchTerm: '',
    lastSyncedFilter: 'all'
  });

  const [sortConfig, setSortConfig] = useState({
    field: 'last_sync_at',
    direction: 'desc'
  });

  // Pagination state - synced with server
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Server-side search with debounce
  const [serverSearchTerm, setServerSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);

  // Debounced search handler - triggers API call after 500ms of no typing
  const handleSearchChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, searchTerm: value }));

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setServerSearchTerm(value);
      setCurrentPage(1);
      fetchOpportunities(1, itemsPerPage, value);
    }, 500);
  }, [fetchOpportunities, itemsPerPage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ===========================================================================
  // FILTERING LOGIC
  // ===========================================================================

  const filteredOpportunities = useMemo(() => {
    // Search is now done server-side, only apply local filters
    return opportunities.filter(opp => {
      // Stage filter
      if (filters.stage !== 'all' && String(opp.stage) !== filters.stage) {
        return false;
      }

      // Sync status filter
      if (filters.syncStatus !== 'all' && opp.sync_status !== filters.syncStatus) {
        return false;
      }

      // Amount range
      const amount = opp.amount || 0;
      if (filters.minAmount && amount < Number(filters.minAmount)) {
        return false;
      }
      if (filters.maxAmount && amount > Number(filters.maxAmount)) {
        return false;
      }

      // Last synced filter
      if (filters.lastSyncedFilter !== 'all') {
        const syncDate = opp.last_sync_at ? new Date(opp.last_sync_at) : null;
        if (!syncDate) return filters.lastSyncedFilter === 'never';

        const now = new Date();
        const diffMs = now - syncDate;
        const diffHours = diffMs / (1000 * 60 * 60);
        const diffDays = diffMs / (1000 * 60 * 60 * 24);

        switch (filters.lastSyncedFilter) {
          case '1h':
            if (diffHours > 1) return false;
            break;
          case '24h':
            if (diffHours > 24) return false;
            break;
          case '7d':
            if (diffDays > 7) return false;
            break;
          case '30d':
            if (diffDays > 30) return false;
            break;
          case 'never':
            return false; // Already handled above
          default:
            break;
        }
      }

      return true;
    });
  }, [opportunities, filters]);

  // ===========================================================================
  // SORTING LOGIC
  // ===========================================================================

  const sortedOpportunities = useMemo(() => {
    return [...filteredOpportunities].sort((a, b) => {
      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];

      // Handle nulls
      if (!aVal && aVal !== 0) return 1;
      if (!bVal && bVal !== 0) return -1;

      // Type-specific comparison
      if (sortConfig.field === 'amount' || sortConfig.field === 'probability') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (sortConfig.field.includes('date') || sortConfig.field.includes('sync_at')) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredOpportunities, sortConfig]);

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortArrow = (field) => {
    if (sortConfig.field !== field) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  // ===========================================================================
  // PAGINATION LOGIC (Server-side)
  // ===========================================================================

  const totalPages = pagination?.total_pages || 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchOpportunities(page, itemsPerPage, serverSearchTerm);
  };

  const handleItemsPerPageChange = (newPerPage) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(1);
    fetchOpportunities(1, newPerPage, serverSearchTerm);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handleViewDetails = (opp) => {
    setSelectedOpportunity(opp);
    setShowDetailsModal(true);
    setSyncHistory(null);
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedOpportunity(null);
    setSyncHistory(null);
    setRetryResult(null);
  };

  const loadSyncHistory = async (opportunityId) => {
    setLoadingHistory(true);
    try {
      const response = await apiFetch(`/opportunities/${opportunityId}/history`);
      const result = await response.json();
      if (result.success) {
        setSyncHistory(result.history);
      }
    } catch (error) {
      console.error('Error loading sync history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRetrySync = async (autotaskId) => {
    setLoadingRetry(true);
    setRetryResult(null);
    try {
      const response = await fetch('https://flow.ccassistant.app/webhook/50c141ba-3e0c-4d66-8715-60e630d9a2f8', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ opportunity_id: autotaskId }),
      });

      if (response.ok) {
        setRetryResult({ success: true, message: 'Sync triggered successfully! Check back in a few moments.' });
        // Reload sync history after a short delay
        setTimeout(() => {
          loadSyncHistory(autotaskId);
        }, 3000);
      } else {
        setRetryResult({ success: false, message: 'Failed to trigger sync. Please try again.' });
      }
    } catch (error) {
      console.error('Error triggering retry sync:', error);
      setRetryResult({ success: false, message: 'Error triggering sync: ' + error.message });
    } finally {
      setLoadingRetry(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      stage: 'all',
      syncStatus: 'all',
      minAmount: '',
      maxAmount: '',
      searchTerm: '',
      lastSyncedFilter: 'all'
    });
    // Also clear server-side search and refresh
    if (serverSearchTerm) {
      setServerSearchTerm('');
      setCurrentPage(1);
      fetchOpportunities(1, itemsPerPage, '');
    }
  };

  // ===========================================================================
  // ENHANCED STATS FOR BOSS VIEW
  // ===========================================================================

  const enhancedStats = {
    ...stats,
    entityType: 'Opportunities',
    customStats: [
      { label: 'Pipeline Value', value: formatCurrency(stats.totalValue), color: '#10B981' },
      { label: 'Avg Deal Size', value: formatCurrency(stats.averageValue), color: '#8B5CF6' },
      { label: 'Win Rate', value: `${stats.winRate}%`, color: '#059669' },
      { label: 'Sync Coverage', value: `${stats.syncCoverage}%`, color: stats.syncCoverage > 90 ? '#10B981' : '#F59E0B' },
      { label: 'High Value (>$50K)', value: stats.highValueCount, color: '#DC2626' }
    ]
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <>
      {/* Stats Bar */}
      <StatsBar stats={enhancedStats} />

      {/* Filters Section */}
      <div className="filters-section">
        <input
          type="text"
          className="search-bar"
          placeholder="🔍 Search by title, HubSpot Deal ID, or Autotask ID... (searches all pages)"
          value={filters.searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />

        <div className="filter-row">
          <select
            className="filter-select"
            value={filters.stage}
            onChange={(e) => setFilters({...filters, stage: e.target.value})}
          >
            <option value="all">All Stages</option>
            <option value="29682770">Appointment Scheduled</option>
            <option value="29682771">Qualified to Buy</option>
            <option value="29682772">Proposal Sent</option>
            <option value="29682773">Decision Maker Bought-In</option>
            <option value="29682774">Contract Sent</option>
            <option value="29682775">Closed Won</option>
            <option value="29682776">Closed Lost</option>
          </select>

          <select
            className="filter-select"
            value={filters.syncStatus}
            onChange={(e) => setFilters({...filters, syncStatus: e.target.value})}
          >
            <option value="all">All Sync Status</option>
            <option value="synced">Synced</option>
            <option value="pending">Pending</option>
            <option value="error">Error</option>
          </select>

          <select
            className="filter-select"
            value={filters.lastSyncedFilter}
            onChange={(e) => setFilters({...filters, lastSyncedFilter: e.target.value})}
          >
            <option value="all">All Last Synced</option>
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="never">Never Synced</option>
          </select>

          <input
            type="number"
            className="filter-select"
            placeholder="Min Amount"
            value={filters.minAmount}
            onChange={(e) => setFilters({...filters, minAmount: e.target.value})}
          />

          <input
            type="number"
            className="filter-select"
            placeholder="Max Amount"
            value={filters.maxAmount}
            onChange={(e) => setFilters({...filters, maxAmount: e.target.value})}
          />

          <button
            className="btn-secondary"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="table-section">
        <div className="table-header">
          <div className="table-title">
            {loading
              ? 'Loading...'
              : `Showing ${sortedOpportunities.length} of ${pagination?.total || stats.total} opportunities (Page ${currentPage} of ${totalPages})`}
          </div>
          <div className="table-actions">
            <button className="icon-btn-small" onClick={() => fetchOpportunities(currentPage, itemsPerPage, serverSearchTerm)}>
              🔄 Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            Loading opportunities...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('title')} className="sortable-header">
                  Opportunity {sortArrow('title')}
                </th>
                <th onClick={() => handleSort('amount')} className="sortable-header">
                  Amount {sortArrow('amount')}
                </th>
                <th onClick={() => handleSort('stage')} className="sortable-header">
                  Stage {sortArrow('stage')}
                </th>
                <th>Autotask ID</th>
                <th>HubSpot ID</th>
                <th>Sync Status</th>
                <th onClick={() => handleSort('last_sync_at')} className="sortable-header">
                  Last Synced {sortArrow('last_sync_at')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedOpportunities.map((opp, index) => (
                <tr key={opp.id || index}>
                  <td>
                    <span onClick={() => handleViewDetails(opp)} className="company-link">
                      {opp.title || '-'}
                    </span>
                  </td>
                  <td className={`amount-cell ${isHighValue(opp.amount) ? 'high-value' : ''}`}>
                    {formatCurrency(opp.amount)}
                  </td>
                  <td>
                    <span className={`stage-badge ${getStageClass(opp.stage)}`}>
                      {opp.stage_label || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    {opp.autotask_id ? (
                      <a
                        href={getAutotaskOpportunityUrl(opp.autotask_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="autotask-link"
                      >
                        {opp.autotask_id} 🔗
                      </a>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>-</span>
                    )}
                  </td>
                  <td>
                    {opp.hubspot_deal_id ? (
                      <a
                        href={getHubSpotDealUrl(opp.hubspot_deal_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="autotask-link"
                      >
                        {opp.hubspot_deal_id} 🔗
                      </a>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>-</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${opp.sync_status || 'pending'}`}>
                      {getSyncStatusLabel(opp.sync_status)}
                    </span>
                  </td>
                  <td>
                    {opp.last_sync_at ? (
                      <span title={formatDateTime(opp.last_sync_at)}>
                        {formatDaysAgo(opp.last_sync_at)}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <button
                      className="action-btn"
                      onClick={() => handleViewDetails(opp)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {!loading && pagination && (
          <div className="pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages} ({pagination.total} total items)
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                style={{ marginLeft: 12, padding: '4px 8px', borderRadius: 6 }}
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
              </select>
            </div>
            <div className="pagination-controls">
              <button
                className="page-btn"
                onClick={() => handlePageChange(1)}
                disabled={!pagination.has_prev}
              >
                First
              </button>
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.has_prev}
              >
                Prev
              </button>
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: '10px 8px' }}>...</span>
                ) : (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.has_next}
              >
                Next
              </button>
              <button
                className="page-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={!pagination.has_next}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedOpportunity && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedOpportunity.title}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="modal-body">
              {/* Business Details Section */}
              <div className="detail-section">
                <h3>Business Details</h3>
                <div className="detail-row">
                  <strong>Amount:</strong> {formatCurrency(selectedOpportunity.amount)}
                </div>
                <div className="detail-row">
                  <strong>Stage:</strong>{' '}
                  <span className={`stage-badge ${getStageClass(selectedOpportunity.stage)}`}>
                    {selectedOpportunity.stage_label}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Probability:</strong> {selectedOpportunity.probability}%
                </div>
                <div className="detail-row">
                  <strong>Company:</strong> {selectedOpportunity.company_name}
                </div>
                <div className="detail-row">
                  <strong>Owner:</strong> {selectedOpportunity.owner_name}
                </div>
                <div className="detail-row">
                  <strong>Projected Close:</strong> {formatDate(selectedOpportunity.projected_close_date)}
                </div>
                {selectedOpportunity.description && (
                  <div className="detail-row">
                    <strong>Description:</strong> {selectedOpportunity.description}
                  </div>
                )}
              </div>

              {/* System Links Section */}
              <div className="detail-section">
                <h3>System Links</h3>
                <div className="detail-row">
                  <strong>Autotask ID:</strong>{' '}
                  <a href={getAutotaskOpportunityUrl(selectedOpportunity.autotask_id)}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="autotask-link">
                    {selectedOpportunity.autotask_id} 🔗
                  </a>
                </div>
                <div className="detail-row">
                  <strong>HubSpot Deal ID:</strong>{' '}
                  {selectedOpportunity.hubspot_deal_id ? (
                    <a href={getHubSpotDealUrl(selectedOpportunity.hubspot_deal_id)}
                       target="_blank"
                       rel="noopener noreferrer"
                       className="autotask-link">
                      {selectedOpportunity.hubspot_deal_id} 🔗
                    </a>
                  ) : 'Not synced'}
                </div>
              </div>

              {/* Sync Status Section */}
              <div className="detail-section">
                <h3>Sync Status</h3>
                <div className="detail-row">
                  <strong>Status:</strong>{' '}
                  <span className={`status-badge ${selectedOpportunity.sync_status}`}>
                    {getSyncStatusLabel(selectedOpportunity.sync_status)}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Last Synced:</strong> {formatDateTime(selectedOpportunity.last_sync_at)}
                </div>

                {selectedOpportunity.sync_message && (
                  <div className={
                    selectedOpportunity.sync_status === 'error'
                      ? 'error-message-box'
                      : 'success-message-box'
                  }>
                    {selectedOpportunity.sync_message}
                  </div>
                )}

                <div style={{ marginTop: 16 }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleRetrySync(selectedOpportunity.autotask_id)}
                    disabled={loadingRetry}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      cursor: loadingRetry ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loadingRetry ? (
                      <>
                        <span style={{
                          display: 'inline-block',
                          width: '14px',
                          height: '14px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: 'white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Syncing...
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '16px' }}>↻</span>
                        Retry Sync
                      </>
                    )}
                  </button>
                </div>

                {retryResult && (
                  <div
                    className={retryResult.success ? 'success-message-box' : 'error-message-box'}
                    style={{
                      marginTop: 12,
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>{retryResult.success ? '✓' : '✕'}</span>
                    {retryResult.message}
                  </div>
                )}
              </div>

              {/* Sync History Section */}
              <div className="detail-section">
                <h3>Sync History</h3>
                {!syncHistory ? (
                  <button
                    className="btn-secondary"
                    onClick={() => loadSyncHistory(selectedOpportunity.autotask_id)}
                    disabled={loadingHistory}
                  >
                    {loadingHistory ? 'Loading...' : 'Load History'}
                  </button>
                ) : (
                  <div className="sync-history">
                    {syncHistory.length === 0 ? (
                      <div style={{padding: 12, textAlign: 'center', color: '#6B7280'}}>
                        No sync history available
                      </div>
                    ) : (
                      syncHistory.map((entry, idx) => (
                        <div key={idx} className="sync-log-entry">
                          <div>
                            <span className={`status-badge ${entry.sync_status}`}>
                              {entry.sync_status}
                            </span>
                            {entry.message && (
                              <div className="text-muted" style={{marginTop: 4, fontSize: 12}}>
                                {entry.message}
                              </div>
                            )}
                          </div>
                          <div className="sync-log-time">
                            <span className="text-muted" style={{fontSize: 12}}>
                              {formatDateTime(entry.created_at)}
                            </span>
                            {entry.execution_id && entry.workflow_id && (
                              <>
                                <span className="text-muted" style={{fontSize: 12}}> • </span>
                                <a
                                  href={`https://flow.ccassistant.app/workflow/${entry.workflow_id}/executions/${entry.execution_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="execution-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Execution
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Opportunities;
